import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, ReferenceLine } from 'recharts';
import { AtividadeDiariaData } from '../../data/graficosData';

interface BarChartAtividadeDiariaProps {
  data: AtividadeDiariaData[];
  height?: number;
  showLegend?: boolean;
}

export const BarChartAtividadeDiaria = ({ data, height = 300, showLegend = true }: BarChartAtividadeDiariaProps) => {
  // Calcular média
  const media = Math.round(data.reduce((acc, d) => acc + d.contagens, 0) / data.length);

  // Custom tooltip
  const CustomTooltip = ({ active, payload }: any) => {
    if (active && payload && payload.length) {
      const item = payload[0].payload;
      const acimaDaMedia = item.contagens >= media;

      return (
        <div className="bg-white dark:bg-gray-800 border-2 border-gray-200 dark:border-gray-700 rounded-lg shadow-lg p-4 min-w-[260px]">
          <p className="font-bold text-gray-900 dark:text-white text-sm mb-2 border-b border-gray-200 dark:border-gray-700 pb-2">
            {item.diaCompleto}
          </p>

          <div className="space-y-2">
            <div className="flex justify-between items-center">
              <span className="text-sm text-gray-600 dark:text-gray-400">Contagens:</span>
              <span className="font-bold text-lg text-blue-700">
                {item.contagens}
              </span>
            </div>

            <div className="flex justify-between items-center">
              <span className="text-sm text-gray-600 dark:text-gray-400">Média:</span>
              <span className="font-semibold text-slate-600">{media}</span>
            </div>

            {acimaDaMedia ? (
              <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded px-2 py-1">
                <span className="text-xs text-blue-700 dark:text-blue-400 font-semibold">
                  ↑ +{((item.contagens / media - 1) * 100).toFixed(0)}% acima da média
                </span>
              </div>
            ) : (
              <div className="bg-slate-50 dark:bg-slate-900/20 border border-slate-200 dark:border-slate-800 rounded px-2 py-1">
                <span className="text-xs text-slate-700 dark:text-slate-400 font-semibold">
                  ↓ {((1 - item.contagens / media) * 100).toFixed(0)}% abaixo da média
                </span>
              </div>
            )}

            <div className="pt-2 mt-2 border-t border-gray-200 dark:border-gray-700 space-y-1">
              <div className="flex justify-between text-xs">
                <span className="text-gray-500 dark:text-gray-400">Produtos contados:</span>
                <span className="font-semibold text-gray-900 dark:text-white">{item.produtos}</span>
              </div>
              <div className="flex justify-between text-xs">
                <span className="text-gray-500 dark:text-gray-400">Hospitais ativos:</span>
                <span className="font-semibold text-blue-700">{item.hospitaisAtivos}</span>
              </div>
              <div className="flex justify-between text-xs">
                <span className="text-gray-500 dark:text-gray-400">Horário pico:</span>
                <span className="font-semibold text-slate-600">{item.horarioPico}</span>
              </div>
            </div>
          </div>
        </div>
      );
    }

    return null;
  };

  return (
    <ResponsiveContainer width="100%" height={height}>
      <BarChart
        data={data}
        margin={{
          top: 5,
          right: 30,
          left: 20,
          bottom: 5,
        }}
      >
        <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />

        <XAxis
          dataKey="dia"
          stroke="#6b7280"
          style={{
            fontSize: '12px',
            fontFamily: 'ui-monospace, monospace',
          }}
        />

        <YAxis
          stroke="#6b7280"
          style={{
            fontSize: '12px',
            fontFamily: 'ui-monospace, monospace',
          }}
        />

        <Tooltip content={<CustomTooltip />} />

        {showLegend && (
          <Legend
            wrapperStyle={{
              paddingTop: '20px',
              fontSize: '14px',
              fontWeight: 600,
            }}
          />
        )}

        {/* Linha de referência da média */}
        <ReferenceLine
          y={media}
          stroke="#64748b"
          strokeDasharray="5 5"
          strokeWidth={2}
          label={{
            value: `Média: ${media}`,
            position: 'right',
            fill: '#64748b',
            fontSize: 12,
            fontWeight: 600,
          }}
        />

        <Bar
          dataKey="contagens"
          fill="#1e40af"
          name="Contagens"
          radius={[8, 8, 0, 0]}
        />
      </BarChart>
    </ResponsiveContainer>
  );
};
